import { MILLISECONDS_IN_MINUTE } from '@stdlib/constants-time';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { HotelFeedbacks, PackageFeedbacks } from 'app/typings/feedbacks';
import { apiRequest } from 'app/utils/request/api';

const packageFeedbacksPath = '/extranet/:hotelId/packages/feedbacks' as const;
const hotelFeedbacksPath = '/extranet/:hotelId/feedbacks' as const;

export function usePackageFeedbacks(hotelId?: number) {
  return useQuery<
    { packages: PackageFeedbacks[] },
    unknown,
    { packages: PackageFeedbacks[] },
    (typeof packageFeedbacksPath | { hotelId?: number })[]
  >({
    queryKey: [packageFeedbacksPath, { hotelId }],
    queryFn: () =>
      apiRequest({
        path: packageFeedbacksPath,
        params: { hotelId: hotelId?.toString() ?? '' },
      }),
    meta: { errorMessage: 'Error fetching package feedbacks' },
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    enabled: !!hotelId,
  });
}

type QueryParamsType = {
  hotelId: number | undefined;
  pageNumber: number;
  search: string | undefined;
  packageIds: number[] | undefined;
  sorting: string | undefined;
  beginning: string | undefined;
  ending: string | undefined;
};

export function useHotelFeedbacks(
  queryParams: QueryParamsType,
  hotelId?: number
) {
  return useQuery<
    HotelFeedbacks,
    unknown,
    HotelFeedbacks,
    (
      | typeof hotelFeedbacksPath
      | { hotelId?: number; queryParams: QueryParamsType }
    )[]
  >({
    queryKey: [hotelFeedbacksPath, { hotelId, queryParams }],
    queryFn: () =>
      apiRequest({
        path: hotelFeedbacksPath,
        params: { hotelId: hotelId?.toString() ?? '' },
        queryParams,
      }),
    meta: { errorMessage: "Error fetching hotel's feedbacks" },
    placeholderData: keepPreviousData,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    enabled: !!hotelId,
  });
}
